

















































import { computed, defineComponent, ref } from "@vue/composition-api";
import { ContentFile, ContentFileThumbnailType } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import { isVideoFile } from "@/lib/fileHelpers";
import { $t } from "@/lib/i18nHelpers";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseDuration from "@/components/BaseDuration";
import FullScreen from "@/components/FullScreen";
import ContentFeedVideoGalleryItem from "../ContentFeedVideoGalleryItem";

interface IContentFeedVideoGalleryItem {
  id: string;
  file: ContentFile;
  accessed: boolean;
}

export default defineComponent({
  name: "ContentFeedVideoGallery",
  components: {
    ContentFeedVideoGalleryItem,
    BaseDuration,
    FullScreen,
    BaseIcon,
  },
  props: {
    files: { type: Array as () => ContentFile[], required: true },
    accessed: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    previewMode: { type: Boolean, default: false },
  },
  setup(props) {
    const { showNotSafeForWork } = useNSFWUserSettings();
    const requestLogin = useAuthorisedOnly(() => undefined);
    const playing = ref(false);
    const previewModeType = ref<ContentFileThumbnailType>(ContentFileThumbnailType.SFW);

    const previewToggleText = computed(() => {
      return previewModeType.value === ContentFileThumbnailType.SFW ? $t("SFW version") : $t("🔞 NSFW version");
    });

    // A post keeps its full video private and its trailer public, so access decides which one to play.
    const videoToShow = computed<ContentFile | null>(() => {
      const videos = props.files.filter(file => isVideoFile(file));
      const publicVideos = videos.filter(video => video.publicAccess);
      // duration stays 0 until the file is converted, so it can only order the videos that report one
      const byDuration = publicVideos.filter(video => video.duration > 0).sort((a, b) => a.duration - b.duration);
      // an admin can publish every file of a post, and then length is all that tells a trailer from the video it teases
      const fullVideo = videos.find(video => !video.publicAccess) ?? byDuration[byDuration.length - 1];
      const trailer = byDuration[0];

      return (
        (props.accessed ? fullVideo : trailer) ??
        publicVideos[0] ??
        videos.find(video => video.urlPart.startsWith("blob:")) ??
        (props.previewMode ? videos[0] : undefined) ??
        null
      );
    });

    const videoItems = computed<IContentFeedVideoGalleryItem[]>(() => {
      const file = videoToShow.value;
      return file ? [{ id: file.id, file, accessed: props.accessed }] : [];
    });

    const thumbnails = computed(() => {
      return props.files.filter(i => !!i.thumbnailType);
    });

    const galleryItemStyle = computed(() => {
      const file = videoItems.value[0]?.file;
      if (file && file.width > 0 && file.height > 0) {
        return {
          "padding-bottom": `${Math.min(Math.ceil((file.height / file.width) * 100), 150)}%`,
        };
      }
      return { "padding-bottom": "57%" };
    });

    // the videos are not safe for work, so a guest is asked to log in instead of playing
    const onThumbnailClick = () => {
      if (!showNotSafeForWork.value) requestLogin();
    };

    const onSwitchPreviewMode = () => {
      previewModeType.value =
        previewModeType.value === ContentFileThumbnailType.SFW
          ? ContentFileThumbnailType.NSFW
          : ContentFileThumbnailType.SFW;
    };

    return {
      galleryItemStyle,
      playing,
      videoItems,
      thumbnails,
      showNotSafeForWork,
      previewModeType,
      previewToggleText,
      onThumbnailClick,
      onSwitchPreviewMode,
    };
  },
});
